import React from 'react';
import { observer } from 'mobx-react';
import { mutate } from 'swr';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ModalStep } from '@components/common/multiStepModal';
import { LightFormBuilder } from '@components/common/lightFormBuilder';
import { ApiGetResource, createNonOauthAccountApi } from '@api';
import useStores from '@stores/useStores';

import { useAccountManagementStore } from '../../store';
import { useOAuth } from '../../hooks/useOAuth';
import { getResourceCacheKey } from '../../hooks/useLoadAccountsIntoAccountManagementStore';

export const AccountTypeFormModalStep: React.FC = observer(() => {
  const { workspaceStore, messagesStore } = useStores();
  const {
    selectedAccountDefinition,
    form,
    getNonOAuthPayload,
    getOAuthPayload,
    setAccountId,
    resourceDefinitionName,
  } = useAccountManagementStore();

  const [refetchingAccounts, setRefetchingAccounts] = React.useState(false);
  const [nonOAuthLoading, setNonOAuthLoading] = React.useState(false);

  const handleAccountCreated = React.useCallback(
    async (accountId: string) => {
      setRefetchingAccounts(true);
      mutate(
        getResourceCacheKey(workspaceStore.id, resourceDefinitionName, ApiGetResource.ACCOUNTS),
      ).finally(() => {
        setAccountId(accountId);
        setRefetchingAccounts(false);
      });
    },
    [workspaceStore.id, resourceDefinitionName, setAccountId],
  );

  const { startOAuth, loading: oauthLoading } = useOAuth({
    onComplete: handleAccountCreated,
  });

  if (!selectedAccountDefinition) {
    return null;
  }

  const oauthActionButton = {
    text: 'Connect account',
    icon: <FontAwesomeIcon icon={regular('plug-circle-plus')} />,
    onClick: async () => {
      try {
        await form.validateFields();
        await startOAuth(getOAuthPayload());
      } catch (error) {}
    },
  };

  const nonOAuthActionButton = {
    text: 'Create account',
    icon: <FontAwesomeIcon icon={regular('circle-plus')} />,
    onClick: async () => {
      try {
        await form.validateFields();

        setNonOAuthLoading(true);

        const payload = getNonOAuthPayload();
        const { data } = await createNonOauthAccountApi(workspaceStore.id, payload);

        await handleAccountCreated(data.id);
      } catch (error) {
        messagesStore.showErrorMessage(
          typeof error === 'string' ? error : 'Failed to create account',
        );
      } finally {
        setNonOAuthLoading(false);
      }
    },
  };

  const isOAuth = selectedAccountDefinition.authenticationType === 'oauth';

  const footer = isOAuth
    ? { actionButton: oauthActionButton, loading: oauthLoading || refetchingAccounts }
    : { actionButton: nonOAuthActionButton, loading: nonOAuthLoading || refetchingAccounts };

  return (
    <ModalStep
      gap="16px"
      header={{
        title: 'Configure account',
        description: `Configure your ${selectedAccountDefinition.uiConfig.name} account`,
      }}
      footer={footer}
    >
      <LightFormBuilder formConfig={selectedAccountDefinition.uiConfig.form} form={form} />
    </ModalStep>
  );
});
